import {
  doesTaskCommentMatchSearchQuery,
  doesTaskMatchDueDateFilter,
  doesTaskMatchResponsibleFilter,
  doesTaskMatchStatusFilter,
  doesTaskMatchValidatorFilter,
  doesTaskResponsiblesMatchSearchQuery,
  doesTaskTitleMatchSearchQuery,
  doesTaskValidatorsMatchSearchQuery,
} from '@/common/utils/checklistFilters'
import { flatten } from '@/common/utils/flatArray'

export const getters = {
  checklist: (state, getters) => {
    return state.checklistStructure.map(getters.mapSectionChildren)
  },
  filteredChecklist: (state, getters) => {
    if (!getters.hasActiveFilter && state.searchQuery.length === 0) {
      return getters.checklist
    }

    return getters.filterChecklistItems(getters.checklist)
  },
  flatChecklist: (state, getters) => {
    return flatten([], getters.checklist)
  },
  flatFilteredChecklist: (state, getters) => {
    return flatten([], getters.filteredChecklist)
  },
  tasks: (state, getters) => getters.flatChecklist.filter(item => item.type === 'task'),
  sections: (state, getters) => getters.flatChecklist.filter(item => item.type === 'title'),
  filteredTasks: (state, getters) => getters.flatFilteredChecklist.filter(item => item.type === 'task'),
  filteredSections: (state, getters) => getters.flatFilteredChecklist.filter(item => item.type === 'title'),
  hasActiveFilter: state => {
    return flatten([], state.filters).length > 0
  },
  isChecklistFiltered: (state, getters) => {
    return getters.hasActiveFilter || state.searchQuery.length > 0
  },
  taskStatusCategoriesList: state => Object.entries(state.taskStatusCategories).map(
    ([category, statuses]) => ({ name: category, statuses }),
  ),
  selectedTasks: state => state.selectedChecklistItems.tasks,
  selectedSections: state => state.selectedChecklistItems.sections,
  allSelectedChecklistItems: (state, getters) => [].concat(getters.selectedTasks, getters.selectedSections),
  selectableChecklistItems: (state, getters) => {
    return getters.isChecklistFiltered ? getters.flatFilteredChecklist : getters.flatChecklist
  },
  mapSectionChildren: (state, getters) => section => ({
    ...section,
    ...state.checklistDetails.sections[section.id],
    children: section.children.map(sectionChild => {
      if (sectionChild.type === 'title') {
        return getters.mapSectionChildren(sectionChild)
      }

      if (sectionChild.type === 'task') {
        return {
          ...sectionChild,
          ...state.checklistDetails.tasks[sectionChild.id],
        }
      }
    }),
  }),
  filterChecklistItems: (state, getters) => checklistItems => {
    return checklistItems.reduce((result, checklistItem) => {
      if (checklistItem.type === 'title') {
        const filteredSectionChildren = getters.filterChecklistItems(checklistItem.children)

        if (filteredSectionChildren.length > 0) {
          result.push({ ...checklistItem, children: filteredSectionChildren });
        }
      }

      if (checklistItem.type === 'task') {
        const taskMatchsSearchQuery = getters.doesChecklistTaskMatchSearchQuery(checklistItem)
        const taskMatchsFilters = getters.doesChecklistTaskMatchFilters(checklistItem)

        if (taskMatchsSearchQuery && taskMatchsFilters) {
          result.push(checklistItem)
        }
      }

      return result
    }, [])
  },
  doesChecklistTaskMatchFilters: ({ filters }) => task => {
    const dueDateFilters = filters.find(filter => filter.category.key === 'completionDate')
    const matchDueDateFilter = !dueDateFilters || doesTaskMatchDueDateFilter(task, dueDateFilters)

    const responsiblesFilters = filters.filter(filter => filter.category.key === 'responsibleParties')
    const matchResponsiblesFilters =
      responsiblesFilters.length === 0 ||
      responsiblesFilters.some(filter => doesTaskMatchResponsibleFilter(task, filter))

    const validatorsFilters = filters.filter(filter => filter.category.key === 'validators')
    const matchValidatorsFilters =
      validatorsFilters.length === 0 ||
      validatorsFilters.some(filter => doesTaskMatchValidatorFilter(task, filter))

    const statusesFilters = filters.filter(filter => filter.category.key === 'statuses')
    const matchStatusFilters =
      statusesFilters.length === 0 ||
      statusesFilters.some(filter => doesTaskMatchStatusFilter(task, filter))

    return matchDueDateFilter && matchResponsiblesFilters && matchValidatorsFilters && matchStatusFilters
  },
  doesChecklistTaskMatchSearchQuery: ({ searchQuery }) => task => {
    if (searchQuery.length === 0) {
      return true
    }

    return (task.title && doesTaskTitleMatchSearchQuery(searchQuery, task.title)) ||
      (task.lastComment && doesTaskCommentMatchSearchQuery(searchQuery, task.lastComment)) ||
      doesTaskResponsiblesMatchSearchQuery(searchQuery, task.rights.responsibles) ||
      doesTaskValidatorsMatchSearchQuery(searchQuery, task.rights.validators)
  },
  isDraggingSection: state => state.checklistDragItem && state.checklistDragItem.type === 'title',
  isDraggingTask: state => state.checklistDragItem && state.checklistDragItem.type === 'task',
  prevChecklistDragItem: (state, getters) => {
    if(!state.checklistDragItem) return null
    
    const dragItemIndex = getters.flatChecklist.findIndex((el) => el.id === state.checklistDragItem.id && el.type === state.checklistDragItem.type)
    return getters.flatChecklist[dragItemIndex - 1]
  },
  isPristineChecklist: state => !!state.checklistPristine,
}
